import { CopyOutlined, DeleteTwoTone, PlusOutlined } from "@ant-design/icons";
import { getEnvironment, useAuthFetch } from "@p0security/frontend-core";
import type { SelfHostedComputer } from "@p0security/integrations-self-hosted-shared/types";
import type { ColumnsType } from "@p0security/ui";
import {
  Button,
  Flex,
  Input,
  Modal,
  Spacing,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "@p0security/ui";
import { useCallback, useEffect, useMemo, useState } from "react";

import type {
  SelfHostedIamWriteConfig,
  SelfHostedInstallerContext,
} from "./types.ts";
import { useSelfHostedComputers } from "./util.ts";

const selfHostedRegistrationPath = ({
  environmentId,
  hostId,
}: {
  environmentId: string;
  hostId?: string;
}) => {
  const baseUrl = `integrations/self-hosted/${environmentId}/computers/register`;
  return !hostId ? `${baseUrl}` : `${baseUrl}/${encodeURIComponent(hostId)}`;
};

export const ComputerRegistrationForm: React.FC<{
  context: SelfHostedInstallerContext;
  environmentId: string;
  config: SelfHostedIamWriteConfig;
}> = ({ context, environmentId, config }) => {
  const { tenantId } = context;
  const [showModal, setShowModal] = useState(false);
  const [details, setDetails] = useState<SelfHostedComputer | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isNew = useMemo(() => config.state !== "installed", [config.state]);

  const computers = useSelfHostedComputers({ tenantId, environmentId });

  const handleAuthFetchError = useCallback((error?: string) => {
    if (error) {
      message.error(error);
    }
  }, []);

  const fetch = useAuthFetch(handleAuthFetchError, setIsLoading);

  const handleRegister = useCallback(
    async (key: string) => {
      try {
        const data = {
          key,
          environmentId,
        };
        await fetch(selfHostedRegistrationPath({ environmentId }), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });
      } catch (error) {
        console.error(error);
        message.error("Failed to register computer. Please try again.");
      }
    },
    [fetch, environmentId]
  );

  const handleEditRegistration = useCallback(
    async ({
      hostId,
      labels,
      hostname,
    }: {
      hostId: string;
      labels: string;
      hostname: string;
    }) => {
      try {
        const data = {
          labels,
          hostname,
        };

        await fetch(selfHostedRegistrationPath({ environmentId, hostId }), {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });
      } catch (error) {
        console.error(error);
        message.error("Failed to register computer. Please try again.");
      }
    },
    [fetch, environmentId]
  );

  const handleUnregister = useCallback(
    async ({ hostId }: { hostId: string }) => {
      // make sure it's url encoded
      try {
        await fetch(selfHostedRegistrationPath({ environmentId, hostId }), {
          method: "DELETE",
        });
      } catch (error) {
        console.error(error);
        message.error(
          "Computer fingerprint was not found. Refresh the page and try again."
        );
      }
    },
    [environmentId, fetch]
  );

  const handleOpenModal = useCallback(() => {
    setShowModal(true);
  }, [setShowModal]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, [setShowModal]);

  const handleRowClick = useCallback((computer: SelfHostedComputer) => {
    setDetails(computer);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setDetails(null);
  }, []);

  if (isNew) return null;
  if (isLoading) return <div>Loading...</div>;

  return (
    <>
      <ComputerDetailsModal
        computer={details}
        onClose={handleCloseEdit}
        onSubmit={handleEditRegistration}
      />
      <AddComputerModal
        environmentId={environmentId}
        orgSlug={context.orgData?.slug ?? "<org-id>"}
        open={showModal}
        onClose={handleCloseModal}
        onSubmit={handleRegister}
      />
      <Flex vertical gap={8} className="w-full">
        <Flex justify="space-between" align="center">
          <Spacing asChild mb={0}>
            <Typography.Title level={5}>Computers</Typography.Title>
          </Spacing>
          <Button
            disabled={isNew}
            type="primary"
            onClick={handleOpenModal}
            icon={<PlusOutlined />}
          >
            Register
          </Button>
        </Flex>
        <ComputerTable
          dataSource={computers.docs?.map((d) => d.data) ?? []}
          onUnregister={handleUnregister}
          onRowClick={handleRowClick}
        />
      </Flex>
    </>
  );
};

const computerColumns = ({
  onDelete,
  onCopy,
}: {
  onDelete: (fingerprint: string) => React.MouseEventHandler<HTMLElement>;
  onCopy: () => void;
}): ColumnsType<SelfHostedComputer> => [
  {
    key: "hostname",
    title: "Hostname",
    render: (_, { hostname }) => hostname,
  },
  {
    key: "ip",
    title: "Public IP",
    render: (_, { publicIp }) => publicIp,
  },
  {
    key: "fingerprint",
    title: "Fingerprint",
    render: (_, { fingerprint }) => (
      <Typography.Text copyable={{ text: fingerprint }} onCopy={onCopy}>
        ...{fingerprint.slice(-8)}
      </Typography.Text>
    ),
  },
  {
    key: "labels",
    title: "Labels",
    width: "150px",
    render: (_, { labels }) => (
      <Flex gap={4} className="flex-wrap">
        {labels.map((label, index) => (
          <Tag color="magenta" key={index}>
            {label}
          </Tag>
        ))}
      </Flex>
    ),
  },
  {
    key: "delete",
    width: "40px",
    render: (_, { hostId }) => (
      <Button type="text" size="small" onClick={onDelete(hostId)}>
        <DeleteTwoTone />
      </Button>
    ),
  },
];

const ComputerTable: React.FC<{
  dataSource: SelfHostedComputer[];
  onUnregister: (args: { hostId: string }) => Promise<void> | void;
  onRowClick: (computer: SelfHostedComputer) => void;
}> = ({ dataSource, onUnregister, onRowClick }) => {
  const handleDelete = useCallback(
    (hostId: string): React.MouseEventHandler<HTMLElement> => {
      return (e) => {
        e.stopPropagation();

        void onUnregister({ hostId });
      };
    },
    [onUnregister]
  );

  const handleCopy = useCallback(() => {
    message.success("Fingerprint copied");
  }, []);
  const columns = useMemo(
    () => computerColumns({ onDelete: handleDelete, onCopy: handleCopy }),
    [handleCopy, handleDelete]
  );

  const handleRow = useCallback(
    (record: SelfHostedComputer) => {
      return {
        onClick: () => {
          onRowClick(record);
        },
      };
    },
    [onRowClick]
  );

  return (
    <Spacing asChild mt={8}>
      <Table<SelfHostedComputer>
        /* eslint-disable-line react/forbid-component-props */ style={{
          border: "1px solid #eee",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          width: "100%",
        }}
        className="table-styles"
        columns={columns}
        rowKey="fingerprint"
        dataSource={dataSource}
        pagination={{ position: ["none", "none"] as any }}
        onRow={handleRow}
        rowClassName="hoverable"
      />
    </Spacing>
  );
};

const ComputerDetailsModal: React.FC<{
  computer: SelfHostedComputer | null;
  onClose: () => void;
  onSubmit: (args: {
    hostId: string;
    labels: string;
    hostname: string;
  }) => Promise<void> | void;
}> = ({ computer, onClose, onSubmit }) => {
  const [labels, setLabels] = useState(computer?.labels.join(",") || "");
  const [hostname, setHostname] = useState(computer?.hostname || "");

  useEffect(() => {
    if (computer) {
      setLabels(computer.labels.join(","));
      setHostname(computer.hostname);
    }
  }, [computer]);

  const handleSubmit = useCallback(() => {
    if (computer) {
      void onSubmit({ hostId: computer.hostId, labels, hostname });
    }
  }, [computer, hostname, labels, onSubmit]);

  const onChangeLabels = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLabels(e.target.value);
    },
    []
  );

  const onChangeHostname = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setHostname(e.target.value);
    },
    []
  );

  return (
    <Modal
      title="Computer Details"
      open={!!computer}
      okText="Submit"
      onCancel={onClose}
      cancelText="Cancel"
      onOk={handleSubmit}
    >
      {!computer ? (
        <Spin />
      ) : (
        <Spacing asChild mt={24}>
          <Flex vertical gap={8}>
            <FormItem
              label="Fingerprint"
              description="A unique fingerprint of the computer"
              render={<Input value={computer.fingerprint} disabled />}
            />
            <FormItem
              label="Hostname"
              description="A user-friendly name for the computer."
              render={<Input value={hostname} onChange={onChangeHostname} />}
            />
            <FormItem
              label="Labels"
              description="A comma-separated list of labels for the computer as key value pairs. ex: env=prod,region=us-west"
              render={<Input value={labels} onChange={onChangeLabels} />}
            />
          </Flex>
        </Spacing>
      )}
    </Modal>
  );
};

const AddComputerModal: React.FC<{
  orgSlug: string;
  environmentId: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (key: string) => Promise<void> | void;
}> = ({ orgSlug, environmentId, open, onClose }) => {
  const registrationKeyText = useCallback(
    (formatting: boolean) =>
      `p0-ssh-agent register --auth="<API_KEY>" ${formatting ? "\\ \n\t" : ""} --url="${getEnvironment().apiUrl}/o/${orgSlug}/integrations/self-hosted/${environmentId}/computers/register"`,
    [orgSlug, environmentId]
  );

  const handleCopy = useCallback(() => {
    void navigator.clipboard
      .writeText(registrationKeyText(false))
      .then(() => message.success("Successfully copied key to clipboard"));
  }, [registrationKeyText]);

  return (
    <Modal
      open={open}
      closeIcon={null}
      onOk={handleCopy}
      onCancel={onClose}
      okButtonProps={{ icon: <CopyOutlined /> }}
      okText="Copy"
      cancelText="Close"
    >
      <Typography.Paragraph>
        To register a new computer, install the p0-ssh-agent on your target and
        run:
      </Typography.Paragraph>

      <Typography.Paragraph className="overflow-x-auto whitespace-pre rounded-md border border-[#d9d9d9] bg-[#f5f5f5] p-4 font-mono">
        {registrationKeyText(true)}
      </Typography.Paragraph>
    </Modal>
  );
};

const FormItem: React.FC<{
  label: string;
  render: React.ReactNode;
  description?: string;
  style?: React.CSSProperties;
}> = ({ label, description, render, style }) => {
  return (
    <Flex
      vertical
      gap={2}
      /* eslint-disable-line react/forbid-component-props */ style={{
        width: "100%",
        ...style,
      }}
    >
      <Flex>
        <FormLabel>{label}</FormLabel>
      </Flex>
      {render}
      {description && <FormDescription>{description}</FormDescription>}
    </Flex>
  );
};

const FormLabel: React.FC<{ children: string }> = ({ children }) => {
  return (
    <Typography.Text
      /* eslint-disable-line react/forbid-component-props */ style={{
        fontWeight: 600,
        lineHeight: 1.75,
      }}
    >
      {children}
    </Typography.Text>
  );
};

const FormDescription: React.FC<{ children: string }> = ({ children }) => {
  return (
    <Typography.Text
      type="secondary"
      /* eslint-disable-line react/forbid-component-props */ style={{
        fontSize: 13,
        fontWeight: 500,
      }}
    >
      {children}
    </Typography.Text>
  );
};
